import * as Sentry from "@sentry/vue"

import * as environment from "@/services/environment.js"

import { useConfigurationStore } from "@/store/configuration.js"

import { failedModuleLoadMessages } from "@/router.js"

import { eventBus } from "@/plugins/eventBus.js"

const ignoredErrorMessages = [
  "NotSupportedError: screen.orientation.lock() is not available on this device.",
  "Non-Error promise rejection captured with value: Object Not Found Matching Id",
  "Network Error",
  "NetworkError when attempting to fetch resource",
  "timeout of 30000ms exceeded",
  //https://stackoverflow.com/a/50387233/1562490
  "ResizeObserver loop completed with undelivered notifications.",
  "Request aborted",
  //https://disciple.sentry.io/share/issue/eae4aae9dc8545a89142ba9bbfad4d7a/
  /^canceled$/,
  //Jw player internal error, happens only on safari browsers
  "AbortError: The operation was aborted",
  //Edge mobile, no solution found
  //https://techcommunity.microsoft.com/t5/discussions/error-can-t-find-variable-logmutedmessage-on-edge-mobile/m-p/3912307
  "Can't find variable: logMutedMessage",
  //Having this for a long time, seems to be harmless, more info:
  //https://github.com/firebase/firebase-js-sdk/issues/2364
  "Messaging: A problem occurred while unsubscribing the user from FCM: Requested entity was not found. (messaging/token-unsubscribe-failed).",
  /^Failed to read a named property 'document' from 'Window': Blocked a frame with origin/
]

let sentryInitialized = false

export const setup = ({ app, router }) => {
  if (import.meta.env.MODE !== "production" || import.meta.env.VITE_APP_E2E) {
    return
  }

  const configurationStore = useConfigurationStore()

  const integrations = [Sentry.browserTracingIntegration({ router })]

  if (configurationStore.configuration.profilingEnabled) {
    integrations.push(Sentry.browserProfilingIntegration())
  }

  Sentry.init({
    app,
    dsn: "https://dd8b3e638c4f4da09d88c638cc0fef1b@o769580.ingest.sentry.io/6718259",
    integrations,
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.1,
    // Set profilesSampleRate to 1.0 to profile every transaction.
    // Since profilesSampleRate is relative to tracesSampleRate,
    // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
    // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
    // result in 25% of transactions being profiled (0.5*0.5=0.25)
    profilesSampleRate: 1,
    logErrors: true,
    ignoreErrors: [...failedModuleLoadMessages, ...ignoredErrorMessages]
  })

  Sentry.getCurrentScope().setTag("appName", configurationStore.configuration.appTitle)
  Sentry.getCurrentScope().setTag("revision", environment.getRevision())
  Sentry.getCurrentScope().setTag("version", import.meta.env.VITE_APP_VERSION)

  sentryInitialized = true
}

export const setUserId = (id) => {
  if (import.meta.env.MODE !== "production" || import.meta.env.VITE_APP_E2E || !sentryInitialized) {
    return
  }

  Sentry.setUser({ id })
}

export const reportMessage = (message, tag) => {
  if (!sentryInitialized) {
    return
  }
  Sentry.withScope((scope) => {
    if (tag) {
      scope.setTag(tag.key, tag.value)
    }
    Sentry.captureMessage(message)
  })
}

export const reportError = function (error, snackbarError) {
  if (snackbarError) {
    eventBus.$emit("snackbar-error", snackbarError)
  }

  if (!error || error?.response?.status === 404 || error?.response?.status === 500) {
    return
  }

  if (process.env.NODE_ENV !== "production" || !sentryInitialized) {
    console.error(error)
  } else {
    Sentry.captureException(error)
  }
}
